import { usePrefetchImage } from "@/components/progressive-img";
import { TarotCard } from "@/components/tarot-card";
import { TarotDeckHandler, TarotDeckHandlerContext } from "@/lib/deck";
import { useContext, useEffect, useState } from "react";
import { localizedCards } from "../lib/deck.helpers";

type ProperCard = {
	id: string;
	name: string;
	isUpright: boolean;
};

export function ThreeCards() {
	const deckHandler = useContext(TarotDeckHandlerContext);

	const [shuffling, setShuffling] = useState(false);
	const [shuffleNumber, setShuffleNumber] = useState(0);
	const [deck, setDeck] =
		useState<Awaited<ReturnType<typeof deckHandler.shuffle>>>();

	const [threeCards, setThreeCards] =
		useState<[ProperCard, ProperCard, ProperCard]>();

	useEffect(() => {
		window.Telegram.WebApp.SecondaryButton.setText("Перемешать");
		window.Telegram.WebApp.MainButton.setText("Показать!");
	}, []);

	useEffect(() => {
		const handleShuffleSecondary = async () => {
			window.Telegram.WebApp.SecondaryButton.showProgress();

			setShuffling(true);

			const [deck] = await Promise.all([
				deckHandler.shuffle(),
				new Promise((r) => setTimeout(r, 1200)),
			]);

			setDeck(deck);
			setShuffling(false);
			setShuffleNumber((n) => n + 1);

			window.Telegram.WebApp.SecondaryButton.hideProgress();
		};

		const handleShow = async () => {
			window.Telegram.WebApp.MainButton.showProgress();

			const [firstCard, secondCard, thirdCard] =
				await deckHandler.getCardsByIdx(0, 1, 2);

			if (!firstCard || !secondCard || !thirdCard) return;

			fetch("/api/analytics", {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					Authorization: `tma ${window.Telegram.WebApp.initData}`,
				},
				body: JSON.stringify({
					action: "checked-three-cards",
				}),
			});

			setThreeCards([firstCard, secondCard, thirdCard]);

			window.Telegram.WebApp.MainButton.hideProgress();
		};

		window.Telegram.WebApp.SecondaryButton.onClick(handleShuffleSecondary);
		window.Telegram.WebApp.MainButton.onClick(handleShow);

		return () => {
			window.Telegram.WebApp.SecondaryButton.offClick(handleShuffleSecondary);
			window.Telegram.WebApp.MainButton.offClick(handleShow);
		};
	}, [deckHandler]);

	useEffect(() => {
		const showSecondary = !threeCards;
		const showMain = !threeCards && shuffleNumber > 2;

		if (
			showSecondary &&
			window.Telegram.WebApp.SecondaryButton.isVisible === false
		) {
			window.Telegram.WebApp.SecondaryButton.show();
		}

		if (
			showSecondary === false &&
			window.Telegram.WebApp.SecondaryButton.isVisible
		) {
			window.Telegram.WebApp.SecondaryButton.hide();
		}

		if (showMain && window.Telegram.WebApp.MainButton.isVisible === false) {
			window.Telegram.WebApp.MainButton.show();
		}

		if (showMain === false && window.Telegram.WebApp.MainButton.isVisible) {
			window.Telegram.WebApp.MainButton.hide();
		}
	}, [threeCards, shuffleNumber]);

	const [firstCard, secondCard, thirdCard] = deck?.unsafe_deck
		? TarotDeckHandler.getCardsFromDeck(deck?.unsafe_deck, 0, 1, 2)
		: [];
	usePrefetchImage(
		firstCard ? `/deck/${firstCard.name}-preview.webp` : undefined,
	);
	usePrefetchImage(firstCard ? `/deck/${firstCard.name}.jpg` : undefined);
	usePrefetchImage(
		secondCard ? `/deck/${secondCard.name}-preview.webp` : undefined,
	);
	usePrefetchImage(secondCard ? `/deck/${secondCard.name}.jpg` : undefined);
	usePrefetchImage(
		thirdCard ? `/deck/${thirdCard.name}-preview.webp` : undefined,
	);
	usePrefetchImage(thirdCard ? `/deck/${thirdCard.name}.jpg` : undefined);

	const [fullView, setFullView] = useState(false);

	return (
		<div className="py-8 px-[5vw] relative z-10">
			<div className="flex justify-between mb-3">
				<h1 className="text-2xl font-bold drop-shadow-[-2px_0px_8px_var(--color-yellow-400)]">
					Расклад на три карты
				</h1>
				<div></div>
			</div>
			<div className="my-2 w-full max-w-4xl rounded-xl border border-amber-900/20 bg-amber-50/5 p-4 shadow-sm backdrop-blur-sm">
				<p className="text-sm mb-1">
					Это один из базовых раскладов: сосредоточьтесь на волнующем вас
					вопросе, перемешайте колоду трижды и откройте карты. Интерпретация
					расклада, как и самих карт — это чистое
					<b> творчество и полет фантазии читателя.</b>
				</p>
				<p className="text-xs text-gray-700">Например:</p>
				<ul className="list-disc list-inside text-gray-700">
					<li className="text-xs">
						<b>Хронология:</b> центральная карта — настоящее, левая — прошлое,
						правая — будущее;
					</li>
					<li className="text-xs">
						<b>Окружение:</b> в центре — вы (или человек под вопросом), по бокам
						— важные люди или обстоятельства;
					</li>
					<li className="text-xs">
						или любая метафора, которая кажется вам уместной...
					</li>
				</ul>
			</div>
			<div className="relative w-[90vw]">
				{shuffling && (
					<div className="z-11 absolute inset-0 bg-white/70 flex items-center justify-center rounded-xl">
						<p className="text-xl">Перемешиваем...</p>
					</div>
				)}
				<button
					type="button"
					className={`w-full text-left bg-transparent p-0 border-none grid gap-0.5 ${!fullView || (!threeCards) ? "grid-cols-3" : "grid-cols-1"}`}
					onClick={() => {
						if (threeCards) {
							setFullView(!fullView);
						}
					}}
				>
					<div>
						<TarotCard
							show={Boolean(threeCards?.[0])}
							name={threeCards?.[0]?.name}
							isUpright={threeCards?.[0]?.isUpright ?? true}
						/>
						<div
							className={`bg-transparent backdrop-blur-sm rounded-xl  text-center transition-opacity duration-1000 ${threeCards ? "opacity-100 mt-1" : "opacity-0"}`}
						>
							{threeCards && (
								<div className="py-2 px-4 @container">
									<p className="text-[min(14cqi,24px)] font-semibold leading-tight">
										{localizedCards[threeCards[0].name]?.ru}
									</p>
									{threeCards[0].isUpright === false && (
										<p className="text-[min(12cqi,18px)] font-semibold">
											(перевернутая)
										</p>
									)}
								</div>
							)}
						</div>
					</div>
					<div>
						<TarotCard
							show={Boolean(threeCards?.[1])}
							name={threeCards?.[1]?.name}
							isUpright={threeCards?.[1]?.isUpright ?? true}
						/>
						<div
							className={`bg-transparent backdrop-blur-sm rounded-xl  text-center transition-opacity duration-1000 ${threeCards ? "opacity-100 mt-1" : "opacity-0"}`}
						>
							{threeCards && (
								<div className="py-2 px-4 @container">
									<p className="text-[min(14cqi,24px)] font-semibold leading-tight">
										{localizedCards[threeCards[1].name]?.ru}
									</p>
									{threeCards[1].isUpright === false && (
										<p className="text-[min(12cqi,18px)] font-semibold">
											(перевернутая)
										</p>
									)}
								</div>
							)}
						</div>
					</div>
					<div>
						<TarotCard
							show={Boolean(threeCards?.[2])}
							name={threeCards?.[2]?.name}
							isUpright={threeCards?.[2]?.isUpright ?? true}
						/>
						<div
							className={`bg-transparent backdrop-blur-sm rounded-xl  text-center transition-opacity duration-1000 ${threeCards ? "opacity-100 mt-1" : "opacity-0"}`}
						>
							{threeCards && (
								<div className="py-2 px-4 @container">
									<p className="text-[min(14cqi,24px)] font-semibold leading-tight">
										{localizedCards[threeCards[2].name]?.ru}
									</p>
									{threeCards[2].isUpright === false && (
										<p className="text-[min(12cqi,18px)] font-semibold">
											(перевернутая)
										</p>
									)}
								</div>
							)}
						</div>
					</div>
				</button>
			</div>

			<div className="my-1 w-full max-w-4xl rounded-md border border-blue-900/20 bg-blue-50/5 p-1 px-4 shadow-sm backdrop-blur-sm">
				<p className="text-xs">Подсказка: Нажмите на карты чтобы увеличить</p>
			</div>
		</div>
	);
}
